import React, { useCallback, forwardRef } from 'react';
import styled, { css, DefaultTheme, FlattenSimpleInterpolation } from 'styled-components';
import UnstyledButton from '../unstyled-button/unstyled-button';
import { FontType, FontSize } from '../../../../config/theme/types/themeTypes';
import mergeClassNames from '../../lib/style-helpers/merge-class-name';

export enum ButtonType {
  Mortgage = 'mortgage',
  Savings = 'savings',
  RealEstate = 'realEstate',
  HomeAuto = 'homeAuto',
  Secondary = 'secondary',
  Form = 'form',
}

type ButtonProps = {
  type?: ButtonType;
  className?: string;
  active?: boolean;
  isMobile?: boolean;
};

function getButtonColor({
  theme, type, active,
}: {
  theme: DefaultTheme;
  type?: ButtonType;
  active?: boolean;
}): FlattenSimpleInterpolation {
  const buttonStyles = {
    Fallback: css`
      background: ${active
    ? theme.colors.active
    : theme.colors.primary};
      color: ${theme.colors.white};
    `,
    [ButtonType.Form]: (
      active
        ? (css`
          background: ${theme.colors.primary};
          color: ${theme.colors.white};
        `)
        : (css`
          cursor: auto;
          opacity: .5;
          background: ${theme.colors.primary};
          color: ${theme.colors.white};
        `)
    ),
    [ButtonType.HomeAuto]: css`
      background: ${theme.colors.homeAuto};
      color: ${theme.colors.white};
    `,
    [ButtonType.Mortgage]: css`
      background: ${theme.colors.mortgage};
      color: ${theme.colors.white};
    `,
    [ButtonType.RealEstate]: css`
      background: ${theme.colors.realEstate};
      color: ${theme.colors.white};
    `,
    [ButtonType.Savings]: css`
      background: ${theme.colors.savings};
      color: ${theme.colors.white};
    `,
    [ButtonType.Secondary]: css`
      background: ${theme.colors.transparentPrimary};
      color: ${theme.colors.primary};
    `,
  };

  if (type) {
    return buttonStyles[type];
  }

  return buttonStyles.Fallback;
}

const StyledButton = styled(UnstyledButton) < ButtonProps > `
  ${({ theme }) => css`
    border-radius:  ${theme.buttonType.radius};
    padding:  ${theme.buttonType.padding};
    min-width: 185px;
    white-space: nowrap;
    text-transform: ${theme.uploadPage.buttonCase};
    box-shadow: 2px 2px 6px rgba(0 0 0 / 10%);
    &:hover {
      box-shadow: 2px 2px 6px rgba(0 0 0 / 12%);
    }
    &:active{
      box-shadow: 1px 1px 6px rgba(0 0 0 / 8%);
    }
    :hover{
      background-color: ${theme.colors.hover};
    };
  `}
  
  ${({ isMobile, theme }) =>
    (isMobile
      ? theme.font(FontType.SemiBold, FontSize.Large)
      : theme.font(FontType.SemiBold, FontSize.Medium))}
  ${(props) => getButtonColor(props)}
`;

function Button(
  {
    onClick,
    className,
    active,
    type: _ = ButtonType.Form,
    ...restProps
  }: {
    children: React.ReactNode;
    className?: string;
    active?: boolean;
    disabled?: boolean;
    onClick: (e: React.MouseEvent) => void;
    type: ButtonType;
  },
  ref?:
      | ((instance: HTMLButtonElement | null) => void)
      | React.MutableRefObject<HTMLButtonElement | null>
      | null,
) {
  const onBtnClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (active ?? true) {
        onClick(e);
      }
    },
    [active, onClick],
  );

  return (
    <StyledButton
      className={mergeClassNames('Button', className)}
      active={active}
      ref={ref}
      onClick={onBtnClick}
      {...restProps}
    />
  );
}

export default forwardRef(Button);

